import React from "react"
import { Modal, Tag } from "antd"

const SketchToImgModal = ({ open, onClose, data, onImageClick }) => {
  const lang = localStorage.getItem("language") || "en"
  const generations = data?.detail?.list || []

  // ✅ English prettify helper
  const prettify = (value) => {
    if (!value) return ""
    return value
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  // ✅ Room Type Translation
  const translateRoomType = (value) => {
    if (!value) return ""
    if (lang !== "ar") return prettify(value)

    const normalized = value.toString().trim().toLowerCase()
    const translations = {
      all: "الجميع",
      bathroom: "الحمام",
      bedroom: "غرفة النوم",
      garden: "الحديقة",
      kids_room: "غرفة الأطفال",
      office: "المكتب",
      kitchen: "المطبخ",
      dining: "غرفة الطعام",
      entrance: "المدخل",
      living_room: "الصالة",
      gaming_room: "غرفة الألعاب",
    }
    return translations[normalized] || value
  }

  // ✅ Decor Style Translation
  const translateDecorStyle = (value) => {
    if (!value) return ""
    if (lang !== "ar") return prettify(value)

    const normalized = value.toString().trim().toLowerCase()
    const translations = {
      all: "الجميع",
      modern: "مودرن",
      classy: "راقي",
      contemporary: "معاصر",
      boho: "بوهيمي",
      industrial: "صناعي",
      modern_classic: "مودرن كلاسيك",
      rustic_modern: "ريفي حديث",
      eclectic: "انتقائي",
      shabby_chic: "شيك مهترئ",
      zen: "زن",
    }
    return translations[normalized] || value
  }

  // ✅ Theme Translation
  const translateTheme = (value) => {
    if (!value) return ""
    if (lang !== "ar") return prettify(value)

    const normalized = value.toString().trim().toLowerCase()
    const translations = {
      all: "الجميع",
      dark: "غامق",
      light: "فاتح",
    }
    return translations[normalized] || value
  }

  // ✅ Creativity Translation
  const translateCreativity = (value) => {
    if (!value) return ""
    if (lang !== "ar") return prettify(value)

    const normalized = value.toString().trim().toLowerCase()
    const translations = {
      balanced: "متوازن",
      creative: "إبداعي",
      standard: "قياسي",
      conservative: "محافظ",
      precise: "دقيق",
    }
    return translations[normalized] || value
  }

  return (
    <Modal
      title={
        <div>
          <div className="d-flex">
            <div
              className={
                lang === "ar" ? "r-r-modal-icon-arabic" : "r-r-modal-icon"
              }
            >
              <img src="/dashboard/room-redesign-v2.svg" alt="" />
            </div>
            <div>
              <div className="modal-heading" style={{ marginBottom: 0 }}>
                {lang === "ar" ? "من رسم إلى صورة" : "Sketch To Image"}
              </div>
              <p className="modal-timestamp">
                {data?.date || (lang === "ar" ? "غير متوفر" : "N/A")}
              </p>
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
        height: "70vh",
        padding: "0 12px",
      }}
    >
      {/* Header Section */}
      <div style={{ flex: "0 0 auto", paddingTop: 16 }}>
        <p
          style={{
            fontStyle: "italic",
            marginBottom: 12,
            color: "#555",
          }}
        >
          {lang === "ar"
            ? `تم توليد ${
                generations.length || 0
              } تصميم باستخدام ${generations.reduce(
                (sum, g) => sum + (g.credit_used || 0),
                0
              )} من الاعتمادات`
            : `Generated ${
                generations.length || 0
              } designs using ${generations.reduce(
                (sum, g) => sum + (g.credit_used || 0),
                0
              )} credits`}
        </p>
        <div className="generation_thread">
          {lang === "ar" ? "سلسلة التوليد" : "Generation Thread"}
        </div>
      </div>

      {/* Scrollable Cards Section */}
      <div
        className="scrollable-cards"
        style={{
          flex: "1 1 auto",
          overflowY: "auto",
          marginTop: "10px",
        }}
      >
        {generations.length > 0 ? (
          generations.map((gen, index) => {
            const colors =
              gen.color_palette?.split(",").map((c) => c.trim()) || []

            return (
              <div className="sketch-container" key={index}>
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <span>
                    <span className="count-container">{`0${index + 1}`}</span>{" "}
                    <span className="modal-div-date">
                      &nbsp; {gen.date} {gen.time}
                    </span>
                  </span>
                  {gen.status?.toLowerCase() === "success" ? (
                    <div>
                      <Tag color="green">
                        {lang === "ar" ? "ناجح" : "Success"}
                      </Tag>
                    </div>
                  ) : (
                    <Tag color="red">
                      {lang === "ar"
                        ? gen.status === "Failed"
                          ? "فشل"
                          : gen.status || "خطأ"
                        : gen.status || "Failed"}
                    </Tag>
                  )}
                </div>

                {/* Body */}
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    flexGrow: 1,
                  }}
                >
                  {/* Input */}
                  <div
                    style={{
                      flex: 1,
                      border: "1px solid #eee",
                      borderRadius: "10px",
                      padding: "12px",
                      background: "#fafafa",
                      minWidth: "320px",
                    }}
                  >
                    <div className="modal-input-label">
                      {lang === "ar" ? "المدخل" : "Input"}
                    </div>
                    <img
                      src={gen.input}
                      alt="input"
                      onClick={() => {
                        if (onImageClick) {
                          onImageClick(
                            gen.input,
                            lang === "ar"
                              ? "من رسم إلى صورة"
                              : "Sketch To Image"
                          )
                        }
                      }}
                      style={{
                        width: "100%",
                        maxWidth: "200px",
                        borderRadius: "8px",
                        cursor: onImageClick ? "pointer" : "default",
                        transition: "opacity 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (onImageClick) {
                          e.currentTarget.style.opacity = "0.8"
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onImageClick) {
                          e.currentTarget.style.opacity = "1"
                        }
                      }}
                    />
                  </div>

                  {/* Output */}
                  <div
                    style={{
                      flex: 1,
                      border: "1px solid #eee",
                      borderRadius: "10px",
                      padding: "12px",
                      background:
                        gen.status?.toLowerCase() !== "success"
                          ? "#fff5f5"
                          : "rgba(250,250,250,0.9)",
                      minWidth: "280px",
                    }}
                  >
                    {gen.status?.toLowerCase() !== "success" ? (
                      <div
                        style={{
                          color: "#d93025",
                          border: "1px solid #f5c2c2",
                          borderRadius: "8px",
                          padding: "10px",
                          fontWeight: 500,
                        }}
                      >
                        {lang === "ar" ? "السبب:" : "Reason:"}
                        <br />
                        <span style={{ color: "red" }}>
                          {gen.reason ||
                            (lang === "ar" ? "خطأ غير معروف" : "Unknown error")}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "400",
                            color: "#404040",
                            paddingBottom: "10px",
                          }}
                        >
                          {lang === "ar" ? "الناتج" : "Output"}
                        </div>
                        <img
                          src={gen.output}
                          alt="output"
                          onClick={() => {
                            if (onImageClick) {
                              onImageClick(
                                gen.output,
                                lang === "ar"
                                  ? "من رسم إلى صورة"
                                  : "Sketch To Image"
                              )
                            }
                          }}
                          style={{
                            width: "100%",
                            maxWidth: "200px",
                            borderRadius: "8px",
                            cursor: onImageClick ? "pointer" : "default",
                            transition: "opacity 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (onImageClick) {
                              e.currentTarget.style.opacity = "0.8"
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (onImageClick) {
                              e.currentTarget.style.opacity = "1"
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="sketch-footer">
                  <div>
                    <div style={{ color: "#666", fontSize: "14px" }}>
                      {lang === "ar" ? "نمط الديكور:" : "Decor Style:"}
                    </div>
                    <div>
                      {gen.decor_style
                        ? translateDecorStyle(gen.decor_style)
                        : lang === "ar"
                        ? "غير متوفر"
                        : "N/A"}
                    </div>
                  </div>

                  <div>
                    <div style={{ color: "#666", fontSize: "14px" }}>
                      {lang === "ar" ? "الأسلوب الإبداعي:" : "Creativity:"}
                    </div>
                    <div>
                      {gen.creativity
                        ? translateCreativity(gen.creativity)
                        : lang === "ar"
                        ? "غير متوفر"
                        : "N/A"}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        color: "#666",
                        fontSize: "14px",
                        marginBottom: "8px",
                      }}
                    >
                      {lang === "ar" ? "لوحة الألوان:" : "Color Palette:"}
                    </div>
                    {colors.length > 0 ? (
                      <div style={{ display: "flex", gap: "8px" }}>
                        {colors.map((color, i) => (
                          <div
                            key={i}
                            style={{
                              width: "20px",
                              height: "10px",
                              borderRadius: "4px",
                              backgroundColor: color,
                            }}
                          ></div>
                        ))}
                      </div>
                    ) : (
                      <div>{lang === "ar" ? "غير متوفر" : "N/A"}</div>
                    )}
                  </div>

                  <div>
                    <div style={{ color: "#666", fontSize: "14px" }}>
                      {lang === "ar" ? "النمط العام:" : "Theme Style:"}
                    </div>
                    <div>
                      {gen.room_theme
                        ? translateTheme(gen.room_theme)
                        : lang === "ar"
                        ? "غير متوفر"
                        : "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <p style={{ color: "#888", textAlign: "center", marginTop: 20 }}>
            {lang === "ar"
              ? "لم يتم العثور على بيانات توليد"
              : "No generation data found"}
          </p>
        )}
      </div>
    </Modal>
  )
}

export default SketchToImgModal
